/**
 * @fileoverview Whether a URL belongs to the signed-in person's menu. Menu
 * paths may hold a parameter (`/prism/access/users/:id`), which matches any one
 * segment, so a detail page survives a refresh instead of being sent away.
 */

export interface MenuRoute {
  path?: string | null;
  subMenus?: readonly MenuRoute[] | null;
}

/** A menu path as the router serves it: always under `/app`. */
export function toAppRoute(path: string): string {
  if (!path) return '/app';
  return path.startsWith('/app') ? path : '/app/' + path.replace(/^\/+/, '');
}

const segments = (path: string): string[] => path.split('/').filter(Boolean);

/** Segment by segment; a `:name` segment matches any one non-empty segment. */
export function routeMatches(pattern: string, path: string): boolean {
  const [want, have] = [segments(pattern), segments(path)];
  return want.length === have.length && want.every((part, index) => part.startsWith(':') || part === have[index]);
}

/** True when the path is a menu route or one of its sub-routes, at any depth. */
export function isMenuRoute(routes: readonly MenuRoute[], currentPath: string): boolean {
  return routes.some(
    (route) =>
      (!!route.path && routeMatches(toAppRoute(route.path), currentPath)) ||
      isMenuRoute(route.subMenus ?? [], currentPath),
  );
}
